import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { formatSize } from "../../../../../config/global";
import { useCustomToast } from "../../../../Contexts/ToastContext";
import { allowedFileTypes, MAX_FILE_LIMIT } from "../../../../../config/media";

import "./dropzone.css";

/**
 * Drop zone for the upload dialog.
 *
 * Visuals follow .claude/design-system.md — a 2px dashed indigo border,
 * a `pi-cloud-upload` glyph in the primary color, a two-line prompt,
 * and a quiet hint about supported formats and the per-batch file cap.
 * Hover and drag-over states tint the border + background with the
 * primary-subtle token so the affordance is obvious in both themes.
 */
const Dropzone = ({ onFilesSelected, remainingCapacity, totalSize, disabled }) => {
  const { t } = useTranslation();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { showToast } = useCustomToast();
  const { companyDetails } = useSelector((state) => state.global);

  const validateFiles = useCallback((newFiles) => {
    let validFiles = [];
    let currentSize = totalSize;
    let filesAdded = 0;

    for (const file of newFiles) {
      if (filesAdded >= MAX_FILE_LIMIT) {
        showToast('error', t('media.media_toast_file_limit_exceeded'), t('media.media_toast_file_limit_detail', { count: MAX_FILE_LIMIT }));
        break;
      }

      if (currentSize + file.size > remainingCapacity) {
        showToast('error', t('media.media_toast_capacity_exceeded'), t('media.media_toast_capacity_remaining', { size: formatSize(remainingCapacity - currentSize) }));
        continue;
      }

      if (file.size > companyDetails.upload_once_content_capacity) {
        showToast('error', t('media.media_toast_file_too_large'), t('media.media_toast_file_too_large_detail', { name: file.name, gb: Math.round(companyDetails.upload_once_content_capacity / (1024 * 1024 * 1024)) }));
        continue;
      }

      validFiles.push(file);
      currentSize += file.size;
      filesAdded++;
    }

    return validFiles;
  }, [remainingCapacity, showToast, totalSize, companyDetails.upload_once_content_capacity]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (!disabled) {
      const files = Array.from(e.dataTransfer.files);
      const validFiles = validateFiles(files);
      onFilesSelected(validFiles);
    }
  }, [validateFiles, onFilesSelected, disabled]);

  const handleFileInput = useCallback((e) => {
    if (!disabled) {
      const files = Array.from(e.target.files);
      const validFiles = validateFiles(files);
      onFilesSelected(validFiles);
      e.target.value = '';
    }
  }, [validateFiles, onFilesSelected, disabled]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => setIsDraggingOver(false);

  return (
    <div
      className={`pp-dropzone ${isDraggingOver ? 'is-drag-over' : ''} ${disabled ? 'is-disabled' : ''}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
    >
      <label htmlFor="file" className="pp-dropzone-label">
        <div className="pp-dropzone-icon">
          <i className="pi pi-cloud-upload" />
        </div>
        <p className="pp-dropzone-title">{t('media.media_dropzone_title')}</p>
        <p className="pp-dropzone-sub">
          {t('media.media_dropzone_or')} <span className="pp-dropzone-link">{t('media.media_dropzone_browse')}</span>
        </p>
        <p className="pp-dropzone-hint">
          {t('media.media_dropzone_hint', { count: MAX_FILE_LIMIT })}
        </p>
        {remainingCapacity <= 1024 * 1024 * 5 && (
          <p className="pp-dropzone-warn">
            {t('media.media_dropzone_remaining')} <b>{formatSize(remainingCapacity)}</b>
          </p>
        )}
      </label>
      <input
        className="pp-dropzone-input"
        type="file"
        id="file"
        multiple
        accept={allowedFileTypes.map(ext => `.${ext}`).join(',')}
        onChange={handleFileInput}
        disabled={disabled}
      />
    </div>
  );
};

export default Dropzone;
